import React from "react";
import { Steps } from "antd";
import { Cart } from "../../../Service/cart.service";
import { useAppContext } from "../../../Context/AppContext/AppContext";
import { formatPlanName } from "../../../Utils/planUtils";
import { getStepsForMode, PlanPurchaseMode, StepKey } from "../types";

interface ProgressStepsProps {
  currentStep: StepKey;
  handleStepChange: (step: StepKey) => void;
  userCart: Cart;
  mode: PlanPurchaseMode;
}

const ProgressSteps: React.FC<ProgressStepsProps> = ({
  currentStep,
  handleStepChange,
  userCart,
  mode,
}) => {
  const { plans } = useAppContext();

  const stepsOrder = getStepsForMode(mode);
  const currentIndex = stepsOrder.indexOf(currentStep);
  const safeIndex = currentIndex >= 0 ? currentIndex : 0;

  const isPlanSelected = userCart.basePlan.planId;
  const selectedPlanName =
    plans.find((plan) => plan._id === userCart.basePlan.planId)?.name || null;
  const billingMode = userCart.basePlan.billedAnnually
    ? "Billed Annually"
    : "Billed Monthly";

  const addonStepDescription =
    userCart.addons.length > 0
      ? userCart.addons.reduce((acc, addon) => acc + addon.quantity, 0) +
        " addons selected"
      : null;

  const isPaymentDisabled =
    (mode === "addon_only" && userCart.addons.length === 0) ||
    (mode !== "addon_only" && !userCart.basePlan.planId);

  const isAddonsDisabled = mode !== "addon_only" && !userCart.basePlan.planId;

  const getPlanStepStatus = () => {
    if (currentStep === "plan") return "process";
    if (currentStep === "addons" || currentStep === "payment") {
      return isPlanSelected ? "finish" : "wait";
    }
    return "wait";
  };

  const getAddonsStepStatus = () => {
    if (currentStep === "addons") return "process";
    if (currentStep === "payment") return "finish";
    return "wait";
  };

  const getPaymentStepStatus = () =>
    currentStep === "payment" ? "process" : "wait";

  const buildItems = () => {
    const items: Array<{
      title: string;
      status: "wait" | "process" | "finish";
      description?: string | null;
      disabled?: boolean;
    }> = [];

    if (stepsOrder.includes("plan")) {
      items.push({
        title:
          isPlanSelected && selectedPlanName
            ? formatPlanName(selectedPlanName)
            : "Plan Selection",
        status: getPlanStepStatus(),
        description: isPlanSelected ? billingMode : null,
      });
    }

    items.push({
      title: "Addons",
      status: getAddonsStepStatus(),
      description: addonStepDescription,
      disabled: isAddonsDisabled,
    });

    items.push({
      title: "Payment",
      status: getPaymentStepStatus(),
      disabled: isPaymentDisabled,
    });

    return items;
  };

  const onStepIndexChange = (index: number) => {
    const stepKey = stepsOrder[index];
    if (stepKey) handleStepChange(stepKey);
  };

  return (
    <div className="steps">
      <Steps
        type="navigation"
        size="small"
        current={safeIndex}
        onChange={onStepIndexChange}
        className="site-navigation-steps"
        items={buildItems()}
      />
    </div>
  );
};

export default ProgressSteps;
